import React, { useEffect, useState } from "react";
import { TopNav } from "@/components/TopNav";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatVND } from "@/lib/money";
import { Gift, Wallet, Check, Clock, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Chờ xác nhận", color: "text-yellow-300", icon: Clock },
  confirmed: { label: "Đã giao", color: "text-cyan-300", icon: Check },
  cancelled: { label: "Đã hủy / hoàn coin", color: "text-red-300", icon: X },
};

export default function Rewards() {
  const { user, refresh } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("shop");

  const load = () => {
    api.get("/rewards").then(({ data }) => setRewards(data));
    api.get("/me/redemptions").then(({ data }) => setHistory(data));
  };
  useEffect(() => { load(); }, []);

  const redeem = async (r) => {
    if (!window.confirm(`Đổi "${r.name}" với ${r.coin_price} coin?`)) return;
    try {
      await api.post(`/rewards/${r.id}/redeem`);
      toast.success(`Đã đổi! Mang code đến quầy nhận hàng.`);
      await refresh();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Gift className="text-yellow-300" />
            <h1 className="font-display font-bold text-3xl sm:text-4xl">Đổi quà</h1>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-gradient-to-br from-amber-500/15 to-yellow-700/5 border border-amber-400/30">
            <Wallet size={16} className="text-amber-300" />
            <span className="font-display font-bold text-amber-300" data-testid="my-coin">{formatVND(user?.coin || 0)}</span>
          </div>
        </div>

        <div className="flex gap-2 mb-5">
          {["shop", "history"].map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`rw-tab-${k}`}
              className={`px-4 py-2 rounded-md text-sm ${tab === k ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/40" : "bg-white/[0.03] text-white/60 border border-white/8"}`}>
              {k === "shop" ? "Cửa hàng" : "Lịch sử đổi"}
            </button>
          ))}
        </div>

        {tab === "shop" ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rewards.map((r) => {
              const canAfford = (user?.coin || 0) >= r.coin_price;
              const inStock = r.stock === -1 || r.stock > 0;
              return (
                <div key={r.id} className="rounded-xl bg-[#0c0d14] border border-white/8 p-5 flex flex-col" data-testid={`reward-${r.id}`}>
                  <div className="text-4xl mb-2">{r.icon || "🎁"}</div>
                  <div className="font-display font-semibold mb-1">{r.name}</div>
                  <div className="text-xs text-white/50 flex-1 mb-3">{r.description}</div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1.5">
                      <Wallet size={14} className="text-amber-300" />
                      <span className="font-display font-bold text-amber-300 text-base">{formatVND(r.coin_price)}</span>
                    </div>
                    {r.stock !== -1 && (
                      <span className={`text-xs ${r.stock > 5 ? "text-white/40" : "text-orange-400"}`}>Còn {r.stock}</span>
                    )}
                  </div>
                  <button onClick={() => redeem(r)} disabled={!canAfford || !inStock} data-testid={`redeem-${r.id}`}
                    className="w-full py-2 rounded-md text-sm font-medium bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 text-black disabled:opacity-30 disabled:cursor-not-allowed">
                    {!inStock ? "Hết hàng" : canAfford ? "Đổi ngay" : "Không đủ tiền"}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 overflow-hidden">
            {history.length === 0 ? (
              <div className="py-12 text-center text-white/40 text-sm">Chưa có lịch sử đổi quà</div>
            ) : history.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = meta.icon;
              return (
                <div key={r.id} className="flex items-center gap-3 p-4 border-b border-white/5">
                  <div className="text-2xl">{r.reward_icon || "🎁"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.reward_name}</div>
                    <div className="text-xs text-white/40">{new Date(r.created_at).toLocaleString("vi-VN")}</div>
                  </div>
                  <div className="flex items-center gap-1.5 text-amber-300 text-sm font-display font-bold">
                    <Wallet size={12} /> {formatVND(r.coin_cost)}
                  </div>
                  <div className={`inline-flex items-center gap-1 ${meta.color} text-xs`}><Icon size={11} /> {meta.label}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
